import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../utils/api"; // koristi helper koji automatski prepoznaje env

export default function ProductList({ lang = "hr" }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const t = {
    hr: {
      title: "Naša kolekcija madraca",
      search: "Pretraži",
      filters: "Filteri",
      height: "Visina",
      firmness: "Tvrdoća",
      from: "od",
      details: "Detalji",
      buy: "Kupi",
      empty: "Nema rezultata.",
    },
    en: {
      title: "Our mattress collection",
      search: "Search",
      filters: "Filters",
      height: "Height",
      firmness: "Firmness",
      from: "from",
      details: "Details",
      buy: "Buy",
      empty: "No results.",
    },
  }[lang];

  // 🔹 Fallback demo (ako API nije dostupan)
  const fallback = [
    {
      id: 1,
      slug: "original",
      title: lang === "hr" ? "Madrac Original" : "Mattress Original",
      price_from: 499,
      thumbnail:
        "https://images.unsplash.com/photo-1524758631624-e2822e304c36?q=80&w=1200&auto=format&fit=crop",
      height: 23,
      firmness: "Medium",
      tags: ["pocket", "washable-cover"],
    },
    {
      id: 2,
      slug: "klasik",
      title: lang === "hr" ? "Madrac Klasik" : "Mattress Classic",
      price_from: 699,
      thumbnail:
        "https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=1200&auto=format&fit=crop",
      height: 25,
      firmness: "Medium-Firm",
      tags: ["pocket", "hr-foam"],
    },
    {
      id: 3,
      slug: "gold",
      title: lang === "hr" ? "Madrac Gold" : "Mattress Gold",
      price_from: 999,
      thumbnail:
        "https://images.unsplash.com/photo-1540518614846-7eded433c457?q=80&w=1200&auto=format&fit=crop",
      height: 28,
      firmness: "Firm",
      tags: ["pocket", "gel-foam", "luxury-cover"],
    },
  ];

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await apiFetch(`/${lang}/products`);
        if (alive) setItems(Array.isArray(data) ? data : fallback);
      } catch {
        setItems(fallback);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => (alive = false);
  }, [lang]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(
      (p) =>
        p.title?.toLowerCase().includes(term) ||
        p.slug?.toLowerCase().includes(term) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(term))
    );
  }, [q, items]);

  // 🔹 JSON-LD: ItemList + SearchAction
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: filtered.map((p, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      url:
        lang === "hr"
          ? `${location.origin}/hr/proizvod/${p.slug}`
          : `${location.origin}/en/product/${p.slug}`,
      name: p.title,
    })),
  };

  const siteSearch = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    url: location.origin,
    potentialAction: {
      "@type": "SearchAction",
      target: `${location.origin}${
        lang === "hr"
          ? "/hr/proizvodi?q={search_term_string}"
          : "/en/products?q={search_term_string}"
      }`,
      "query-input": "required name=search_term_string",
    },
  };

  // 🔹 Render
  return (
    <main className="bg-navy text-white">
      <section className="max-w-6xl mx-auto px-6 py-8 md:py-12">
        <h1 className="text-3xl md:text-4xl font-semibold text-gold">
          {t.title}
        </h1>

        {/* Search bar */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.search}
            className="h-11 w-full rounded-xl bg-white/5 border border-white/10 px-4 outline-none placeholder:text-gray-400"
          />
        </div>

        {/* Grid */}
        {loading ? (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl border border-white/10 bg-white/5 h-72 animate-pulse"
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="mt-10 text-gray-300">{t.empty}</p>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {filtered.map((p) => {
              const href =
                lang === "hr"
                  ? `/hr/proizvod/${p.slug}`
                  : `/en/product/${p.slug}`;
              return (
                <article
                  key={p.slug}
                  className="rounded-2xl border border-white/10 bg-white/5 shadow-xl overflow-hidden flex flex-col"
                >
                  <img
                    src={p.thumbnail}
                    alt={p.title}
                    className="h-44 w-full object-cover"
                    loading="lazy"
                  />
                  <div className="p-5 flex-1 flex flex-col">
                    <h2 className="text-white font-semibold">{p.title}</h2>
                    <div className="mt-1 text-sm text-gray-400">
                      {t.from}{" "}
                      <span className="text-gold font-semibold">
                        {Number(p.price_from).toFixed(0)} €
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-gray-400">
                      {t.height}:{" "}
                      <span className="text-white">
                        {p.height ? `${p.height} cm` : "—"}
                      </span>{" "}
                      · {t.firmness}:{" "}
                      <span className="text-white">{p.firmness || "—"}</span>
                    </div>

                    <div className="mt-5 flex gap-3">
                      <a
                        className="inline-flex h-10 items-center rounded-full bg-gold px-4 font-semibold text-black hover:bg-gold-dark transition"
                        href={href}
                      >
                        {t.details}
                      </a>
                      <a
                        className="inline-flex h-10 items-center rounded-full border border-white/20 px-4 font-semibold hover:border-gold hover:text-gold transition"
                        href={href}
                      >
                        {t.buy}
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* JSON-LD SEO markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(siteSearch) }}
      />
    </main>
  );
}
